import { Link, useSearchParams } from 'react-router-dom';
import { formatHours } from '@chronos/shared';
import { useAttendanceSummary } from '@/hooks/queries';
import { Card, EmptyState, ErrorNote, PageHeader, Spinner, StatTile } from '@/components/ui';
import { MonthNav, currentMonthKey } from '@/pages/AttendancePage';

/** One row per person the caller may see; click through to their calendar. */
export function TeamAttendancePage() {
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? currentMonthKey();
  const { data, isLoading, error } = useAttendanceSummary(month);

  const rows = data?.rows ?? [];
  const sum = (k: 'presentDays' | 'halfDays' | 'leaveDays' | 'lateDays' | 'workSeconds' | 'expectedSeconds' | 'breakSeconds' | 'noBreakDays') =>
    rows.reduce((s, r) => s + r.totals[k], 0);

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Team attendance"
        description="Leaves, half days and late arrivals per person for the month. Managers see the projects they manage."
        actions={<MonthNav month={month} onChange={(m) => setParams((p) => { p.set('month', m); return p; })} />}
      />

      <ErrorNote error={error} />

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : rows.length === 0 ? (
        <Card><EmptyState title="No one to show" description="Active users in your scope appear here." /></Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <StatTile label="People" value={String(rows.length)} />
            <StatTile label="Hours worked" value={formatHours(sum('workSeconds'))} hint={`of ${formatHours(sum('expectedSeconds'))} expected`} tone="brand" />
            <StatTile label="No-break days" value={String(sum('noBreakDays'))} hint={`${formatHours(sum('breakSeconds'))} of breaks logged`} tone={sum('noBreakDays') ? 'default' : 'green'} />
            <StatTile label="Leaves" value={String(sum('leaveDays'))} />
            <StatTile label="Half days" value={String(sum('halfDays'))} />
            <StatTile label="Late arrivals" value={String(sum('lateDays'))} />
          </div>

          <Card className="mt-4 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="th">Person</th>
                    <th className="th text-right">Working days</th>
                    <th className="th text-right">Present</th>
                    <th className="th text-right">Half</th>
                    <th className="th text-right">Leaves</th>
                    <th className="th text-right">Late</th>
                    <th className="th text-right">Hours</th>
                    <th className="th text-right">Breaks</th>
                    <th className="th text-right">No break</th>
                    <th className="th text-right">Expected</th>
                    <th className="th text-right">Δ</th>
                    <th className="th w-40">Attendance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => {
                    const t = r.totals;
                    // Surplus minus deficit, so absent days (already charged as
                    // leave) do not read as extra hours owed.
                    const delta = (t.surplusSeconds ?? 0) - (t.deficitSeconds ?? 0);
                    const rate = t.elapsedWorkingDays ? ((t.presentDays + t.halfDays * 0.5) / t.elapsedWorkingDays) * 100 : 0;
                    return (
                      <tr key={r.userId} className="hover:bg-slate-50">
                        <td className="td font-medium text-slate-900">
                          <Link to={`/attendance?userId=${r.userId}&month=${month}`} className="text-brand-700 hover:underline">{r.userName}</Link>
                        </td>
                        <td className="td text-right tabular-nums text-slate-500">{t.elapsedWorkingDays}<span className="text-slate-400">/{t.workingDays}</span></td>
                        <td className="td text-right tabular-nums text-emerald-700">{t.presentDays}</td>
                        <td className="td text-right tabular-nums text-amber-700">{t.halfDays}</td>
                        <td className={`td text-right tabular-nums ${t.leaveDays ? 'font-semibold text-rose-700' : 'text-slate-500'}`}>{t.leaveDays}</td>
                        <td className={`td text-right tabular-nums ${t.lateDays ? 'text-rose-700' : 'text-slate-500'}`}>{t.lateDays}</td>
                        <td className="td text-right font-mono tabular-nums">{formatHours(t.workSeconds)}</td>
                        <td className="td text-right font-mono tabular-nums text-slate-500">{formatHours(t.breakSeconds)}</td>
                        <td className={`td text-right tabular-nums ${t.noBreakDays ? 'font-semibold text-amber-700' : 'text-slate-400'}`} title="Worked days with no break logged">{t.noBreakDays}</td>
                        <td className="td text-right font-mono tabular-nums text-slate-500">{formatHours(t.expectedSeconds)}</td>
                        <td className={`td text-right font-mono tabular-nums ${delta < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                          {delta < 0 ? '−' : '+'}{formatHours(Math.abs(delta))}
                        </td>
                        <td className="td">
                          <div className="flex items-center gap-2">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                              <div className={`h-full rounded-full ${rate >= 90 ? 'bg-emerald-500' : rate >= 70 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${Math.min(100, rate)}%` }} />
                            </div>
                            <span className="w-10 text-right text-xs tabular-nums text-slate-500">{rate.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="mt-3 text-xs text-slate-400">
            Attendance % = (present + ½ × half days) ÷ working days elapsed. Future days are not counted. Hours exclude breaks.
            <b> No break</b> = worked days with zero break logged — usually the whole day was put on a project.
          </p>
        </>
      )}
    </div>
  );
}
